import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { getFirestore, collection, query, where, getDocs } from '@angular/fire/firestore';
import { Patient } from '../models/patient.model';
import { GeneralAppearanceAssessment } from '../models/assessments/general-appearance.model';
import { getStorage, ref, uploadBytes } from 'firebase/storage';
import { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';
import { COLLECTION_PATIENTS, COLLECTION_STUDENT_PATIENTS } from '../global';
import { forkJoin, from, Observable, of } from 'rxjs';
import { User } from '../models/user.model';
import { arrayUnion } from 'firebase/firestore';
import { map } from 'rxjs/operators';
import { ProviderNotifiedAssessment } from '../models/assessments/provider-notified.model';
import { HEENTAssessment } from '../models/assessments/HEENT.model';

@Injectable({
  providedIn: 'root',
})
export class PatientService {
  constructor(private db: AngularFirestore, private storage: AngularFirestore) { }

  /**
   * Retrieves all patients from Firestore one time for the given user.
   *
   * @param user The user to retrieve patients for.
   * @returns List of user's patients.
   */
  getPatientsForUser(user: User): Observable<Patient[]> {
    // If user has no patients, return Observable of empty list
    if (user.patientIds.length === 0) {
      return of([]);
    }

    const col = user.isAdmin ? COLLECTION_PATIENTS : COLLECTION_STUDENT_PATIENTS;
    const observables: Observable<any>[] = [];

    user.patientIds.forEach((id: string) => {
      observables.push(this.db.collection(col).doc(id).get());
    });

    return forkJoin(observables).pipe(map((snapshot) => this.getPatientsFromSnapshot(snapshot)));
  }

  /**
   * Get patient data that matches document id and collection name from firebase.
   *
   * @param id  ID of the patient document to retrieve.
   * @param collection Name of the collection to retrieve the document from ("patients" || "studentPatients").
   * @returns   Observable for the patient document.
   */
  getPatient(id: string, collection: string) {
    return this.db.collection(collection).doc(id).snapshotChanges();
  }

  getPatientFromSnapshot(snapshot: any): Patient {
    const patient = new Patient();
    const obj = {
      id: snapshot.payload.id,
      ...(snapshot.payload.data() as object),
    };
    Object.assign(patient, obj);
    patient.toJSObject();
    this.updateMissingPatientFields(patient);
    this.updateMissingPatientAssessmentFields(patient);
    return patient;
  }

  /**
   *
   * @returns A list of all admin patient medical record numbers.
   */
  getAllMRNs() {
    return this.db.collection(COLLECTION_PATIENTS).snapshotChanges();
  }

  /**
   * Create patient document based on the provided patient argument and image file.
   * Use callback function to do a task after patient is created.
   *
   * @param patient Patient object that contains all the necessary data
   * @param img     Image of the patient (optional)
   */
  upsertPatient(patient: Patient, collection: string, img?: File) {
    // Convert Patient object to a JSON object
    const obj = patient.toFirebase();
    if (img) {
      patient.setProfilePicName(img.name);
      const storage = getStorage();
      const storageRef = ref(storage, patient.getProfilePicURL());

      uploadBytes(storageRef, img)
        .then((snapshot) => {
          console.log('Image uploaded successfully!');
        })
        .catch((error) => {
          console.error('Error uploading image:', error);
        });
    }

    // Use "set" method, since we separate the ID from the data; ID
    // is used as the document ID
    return from(
      this.db
        .collection(collection)
        .doc(patient.id)
        .set(obj)
        .catch((e) => {
          console.error(e);
        })
    );
  }

  /**
   * Uploads a document for a specific patient to Firebase Storage and updates the patient record.
   * @param patientId
   * @param document
   * @returns
   */
  uploadPatientDocument(
    patientId: string,
    documentName: string,
    downloadURL: string
  ): Observable<void> {
    const patientDocRef = this.db.collection('patients').doc(patientId);

    return from(
      patientDocRef.update({
        uploadedDocuments: arrayUnion(documentName),
        // You can add more fields if needed, e.g., downloadURL
      })
    );
  }

  updatePatientField(patientId: string, fieldName: string, value: any): Observable<void> {
    const patientDocRef = this.db.collection('patients').doc(patientId);

    return from(patientDocRef.update({ [fieldName]: value }));
  }

  /**
   * Saves the document name and download URL in Firestore under the 'uploadedDocuments' field.
   *
   * @param patientId The ID of the patient.
   * @param documentName The name of the uploaded document.
   * @param downloadURL The download URL of the uploaded document.
   */
  private saveDocumentInfoInFirestore(
    patientId: string,
    documentName: string,
    downloadURL: string
  ): void {
    const patientDocRef = this.db.collection('patients').doc(patientId);

    // Update the 'uploadedDocuments' field in Firestore
    patientDocRef
      .update({
        uploadedDocuments: documentName,
        // You can add more fields if needed, e.g., downloadURL
      })
      .then(() => {
        console.log('Document information saved in Firestore:', documentName);
      })
      .catch((error) => {
        console.error('Error saving document information in Firestore:', error);
      });
  }

  /**
   * Update the specified document with the provided data (Document will be entirely updated)
   *
   * @param id    Patient unique document id value
   * @param data  Data used to update the document
   * @param col   Collection name to perform update
   * @deprecated Use upsertPatient function instead.
   */
  async setPatient(id: string, data: any, col: string) {
    return await this.db.collection(col).doc(id).update(data);
  }

  /**
   * Delete patient document that matches document id from patients collection.
   *
   * @param id Patient unique document id value.
   */
  deletePatient(id: string, collection: string) {
    return from(this.db.collection(collection).doc(id).delete());
  }

  getAllStudentPatientIds() {
    return from(this.db.collection(COLLECTION_STUDENT_PATIENTS).get());
  }

  /**
   * Create studentPatient document based on the provided patient and patientId.
   *
   * @param patient Patient object that contains all the necessary data
   * @param patientId ID of the document to assign the patient to
   */
  createStudentPatient(patient: Patient, studentId: string): Observable<void> {
    // Store student patient ID
    const patientId = `${studentId}-${patient.mrn}`;

    // Convert Patient object to a JSON object
    const obj = patient.toFirebase();

    return from(this.db.collection(COLLECTION_STUDENT_PATIENTS).doc(patientId).set(obj));
  }

  /**
   *  Check both student id and patient id and returns querySnapshot of
   * document that fits condition
   *
   * @param {string} sId   string constraint value for thet "studentId" field
   * @param {string} pId   string constraint value for thet "id" field (patientId)
   * @param col Collection name
   * @returns
   */
  async patientDuplicateCheck(sId: string, pId: string, col: string) {
    const db = getFirestore();

    // query(collectionReference, condition)
    // Goes through studentpatients collection and find a patient assigned to a specific user
    const q = query(collection(db, col), where('studentId', '==', sId), where('id', '==', pId));

    const querySnapshot = await getDocs(q);

    return querySnapshot;
  }

  /**
   * Converts each Firestore document snapshot in the list into a Patient object.
   *
   * @param snapshots Snapshot of the patient document
   * @returns List of Patient objects created from the corresponding document data
   */
  private getPatientsFromSnapshot(snapshots: QueryDocumentSnapshot<DocumentData>[]): Patient[] {
    return snapshots.map((snapshot) => {
      const patient = new Patient();
      const obj = { ...snapshot.data(), id: snapshot.id };
      Object.assign(patient, obj);
      patient.toJSObject();
      this.updateMissingPatientFields(patient);
      this.updateMissingPatientAssessmentFields(patient);
      return patient;
    });
  }

  /**
   * After reading a patient from the database, this function is used to fill in
   * default values for missing fields.
   *
   * @param patient The patient to update
   */
  private updateMissingPatientFields(patient: Patient) {
    if (!patient.generalInfo.patientType || patient.generalInfo.patientType === 'unassigned') {
      patient.generalInfo.patientType = 'Adult';
    }
  }

  private updateMissingPatientAssessmentFields(patient: Patient) {
    if (!patient.assessments || patient.assessments.length === 0) {
      return;
    }

    patient.assessments.forEach(assessment => {
      if (!assessment.generalAppearance) {
        assessment.generalAppearance = new GeneralAppearanceAssessment({});
      }
      if (!assessment.heent) {
        assessment.heent = new HEENTAssessment({});
      }
      if (!assessment.providerNotified) {
        assessment.providerNotified = new ProviderNotifiedAssessment({});
      }
    })
  }

}
